import { CalendarToday, Pets } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import {
  GET_ARCHIVED_THERAPIES_LIST,
  GET_THERAPIES_LIST,
} from "../../../../../../enums/ApiPaths";
import {
  CRIMSON_RED,
  SUCCESS_PRIMARY_MUI,
} from "../../../../../../enums/Colors";
import { SINGLE_ARCHIVED_INPATIENT_THERAPY, SINGLE_BOX_THERAPY } from "../../../../../../enums/QueryKeys";
import { SINGLE_THERAPY_VIEW } from "../../../../../../enums/UrlPaths";
import { useFetch } from "../../../../../../hooks/Fetching";
import useCheckPath from "../../../../../../hooks/useCheckPath";
import usePrimaryColor from "../../../../../../hooks/usePrimaryColor";
import AlertBar from "../../../../components/display/AlertBar";
import AddTherapy from "../../../../components/forms/AddTherapy";
import HorizontalStack from "../../../../components/wrappers/HorizontalStack";
import { single_box_view_title_size } from "../../SingleBoxView";

export default function Therapies({ numberOfPets }) {
  const { id } = useParams();
  const { t } = useTranslation();
  const isArchied = useCheckPath();
  
  const url = isArchied
    ? GET_ARCHIVED_THERAPIES_LIST + id
    : GET_THERAPIES_LIST + id;

  const key = isArchied
    ? SINGLE_ARCHIVED_INPATIENT_THERAPY
    : SINGLE_BOX_THERAPY;
  const { data, isLoading, error } = useFetch(url, null, [key, id]);

  if (isLoading) {
    return <LinearProgress />;
  } else if (!isLoading && error) {
    return (
      <Wrapper numberOfPets={numberOfPets}>
        <AlertBar severity="error" message={t("ListOfTherapiesFetchError")} />
      </Wrapper>
    );
  }

  if (!data || data?.length == 0) {
    return (
      <Wrapper numberOfPets={numberOfPets}>
        <Typography color="grayText" variant="subtitle2">
          {t("NoTasksFoundForThisBox")}
        </Typography>
      </Wrapper>
    );
  }

  return (
    <Wrapper numberOfPets={numberOfPets}>
      <Box mt={2}>
        {data?.map((item) => (
          <Fragment key={item?.id}>
            <Therapy item={item} />
          </Fragment>
        ))}
      </Box>
    </Wrapper>
  );
}

function Wrapper({ children, numberOfPets }) {
  const { t } = useTranslation();
  const isArchived = useCheckPath();
  return (
    <Card>
      <CardContent>
        <HorizontalStack>
          <Typography variant={single_box_view_title_size} gutterBottom>
            {t("Therapies")}
          </Typography>
          {numberOfPets > 0 && !isArchived ? <AddTherapy /> : null}
        </HorizontalStack>
        {children}
      </CardContent>
    </Card>
  );
}

const Therapy = ({ item }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const primary = usePrimaryColor();
  function handleNavigate() {
    navigate(SINGLE_THERAPY_VIEW + item?.id);
  }

  function getBorderColor() {
    const string = String(item.alertSeverity).toUpperCase();
    if (string === "ERROR") {
      return CRIMSON_RED;
    } else if (string === "INFO") {
      return primary;
    } else {
      return SUCCESS_PRIMARY_MUI;
    }
  }

  return (
    <Card
      elevation={0}
      sx={{
        width: "100%",
        mb: 2,
        borderRadius: 3,
        borderLeft: `2px solid ${getBorderColor()}`,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
        >
          {/* TEXT CONTENT */}
          <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                wordBreak: "break-all",
                fontWeight: 600,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.title}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarToday fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {item.startDate} {item?.endDate ? `– ${item.endDate}` : null}
                {/* If there is no end date it won't be rendered */}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Pets fontSize="small" />
              <Typography variant="body2" color="text.secondary">
                {item.petName}
                {/* If there is no end date it won't be rendered */}
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                wordBreak: "break-all",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.description}
            </Typography>
          </Stack>

          {/* BUTTON */}
          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleNavigate()}
            sx={{
              flexShrink: 0,
              alignSelf: { xs: "stretch", lg: "center" },
              width: { lg: "auto" },
            }}
          >
            {t("Details")}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};
